import React, { useEffect, useRef, useState, useCallback } from "react";
import { fetchIndeePlayer } from "../../utils/api/helpers/indee";
import mediaPlaceholder from "../../assets/img/placeholder/photo-placeholder.png";
import ScreenerListItem from "./ScreenerListItem";

const INDEE_INIT_SCRIPT_URL =
  "https://api.indee.tv/v2/watch/stream/player/init";

const IndeePlayer = ({
  screenerKey,
  screenerList,
  projectKey,
  activeKey,
  projectName,
  activeScreener,
  setActiveScreener,
  viewList,
  wmImage,
  wmOpacity,
  wmDuration,
  wmPosition,
  wmText,
  wmInterval,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [playerHtml, setPlayerHtml] = useState(null);
  const [indeeParams, setIndeeParams] = useState(null);
  const [multiSeason, setMultiSeason] = useState(false);

  const iframeRef = useRef(null);

  // Stores the latest player-init payload used by iframe onLoad and retry insertion attempts
  const payloadRef = useRef(null);

  const dataInsertionStopRef = useRef(null);

  const stopDataInsertion = () => {
    if (dataInsertionStopRef.current) {
      dataInsertionStopRef.current();
      dataInsertionStopRef.current = null;
    }
  };

  useEffect(() => {
    if (screenerKey) {
      stopDataInsertion();
      setLoaded(false);

      fetchIndeePlayer(screenerKey)
        .then((result) => {
          if (result?.data?.error && result?.data?.message?.unauth) return;

          setPlayerHtml(result.data.embeddablePlayerHtml);

          const params =
            typeof structuredClone === "function"
              ? structuredClone(result.data.params)
              : JSON.parse(JSON.stringify(result.data.params));

          if (wmText !== "") {
            const overlayParams = {
              text: wmText,
              opacity: wmOpacity,
              position: wmPosition,
              interval_in_seconds: wmInterval,
              duration_in_seconds: wmDuration,
              image_link: wmImage,
            };
            if (params?.[1]) params[1].overlayWatermarkDetails = overlayParams;
          }

          setIndeeParams(params);
        })
        .catch((error) => console.log("INDEE ERROR fetching player", error))
        .finally(() => {
          setLoaded(true);
        });
    }
  }, [
    screenerKey,
    wmPosition,
    wmOpacity,
    wmText,
    wmInterval,
    wmDuration,
    wmImage,
  ]);

  const loadScript = (url) => {
    return new Promise((resolve, reject) => {
      if (window?.initializeIndeePlayer) return resolve();

      const existing = document.querySelector('script[data-indee-init="1"]');
      if (existing) {
        existing.addEventListener("load", resolve, { once: true });
        existing.addEventListener("error", reject, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = url;
      script.async = true;
      script.dataset.indeeInit = "1";
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const hasMixedSeasons = (arr) => {
    const seasons = new Set(arr.map((obj) => obj.season));
    return seasons.size > 1;
  };

  useEffect(() => {
    if (screenerList) {
      setMultiSeason(hasMixedSeasons(screenerList));
    }
  }, [screenerList]);

  const startDataInsertion = (iframeEl, payload) => {
    stopDataInsertion();

    let tries = 0;
    const maxTries = 50;
    let done = false;

    const onDataInsert = (evt) => {
      // Only treat a message from this iframe as an acknowledgement
      if (evt.source !== iframeEl?.contentWindow) return;
      done = true;
      stopOnDataInsert();
    };

    window.addEventListener("message", onDataInsert);

    const interval = setInterval(() => {
      tries += 1;

      if (!iframeEl?.contentWindow) {
        stopOnDataInsert();
        return;
      }

      try {
        iframeEl.contentWindow.postMessage(JSON.stringify(payload), "*");
      } catch {
        // ignore
      }

      if (done || tries >= maxTries) {
        stopOnDataInsert();
      }
    }, 100);

    const stopOnDataInsert = () => {
      clearInterval(interval);
      window.removeEventListener("message", onDataInsert);
    };

    dataInsertionStopRef.current = stopOnDataInsert;
  };

  // Derives the iframe init payload from indeeParams and caches it for retries/onLoad
  useEffect(() => {
    if (!indeeParams) {
      payloadRef.current = null;
      return;
    }

    payloadRef.current = {
      video_key: indeeParams[3],
      playbackData: indeeParams[1],
      config: { ...(indeeParams[4] || {}) },
    };
  }, [indeeParams]);

  const attemptStart = useCallback(() => {
    const iframeEl = iframeRef.current;
    const payload = payloadRef.current;

    if (!iframeEl || !payload) return;

    startDataInsertion(iframeEl, payload);
  }, []);

  useEffect(() => {
    if (!indeeParams || !playerHtml) return;

    let cancelled = false;

    loadScript(INDEE_INIT_SCRIPT_URL)
      .then(() => {
        if (cancelled) return;

        setTimeout(() => {
          if (!cancelled) attemptStart();
        }, 0);
      })
      .catch((error) => console.log("INDEE ERROR", error));

    return () => {
      cancelled = true;
      stopDataInsertion();
    };
  }, [indeeParams, playerHtml, attemptStart]);

  return (
    <div className={"indee-video-outer-container d-flex"}>
      <div
        className={"col-3 flex-fill pe-2 " + (viewList ? "d-block" : "d-none")}
      >
        {screenerList && screenerList.length > 0 ? (
          <div
            className={"scroll-container"}
            style={{
              maxHeight: "100%",
              overflowY: "scroll",
              width: "100%",
              transform: "none",
            }}
          >
            {screenerList.map((scr) => (
              <ScreenerListItem
                key={`screener_${projectKey}_${scr.key}`}
                projectName={projectName}
                scr={scr}
                setLoaded={setLoaded}
                activeScreener={activeScreener}
                setActiveScreener={setActiveScreener}
                projectKey={projectKey}
                activeKey={activeKey}
                multiSeason={multiSeason}
              />
            ))}
          </div>
        ) : (
          <p>No Screeners Available...</p>
        )}
      </div>

      <div className={"indee-video-container"}>
        <img
          src={mediaPlaceholder}
          alt={"placeholder"}
          className="indee-video-sizer"
        />
        <div className={"indee-video-outer-wrapper"}>
          <div className={"indee-video-inner-wrapper"}>
            <iframe
              // Re-mounts the iframe when screenerKey changes to reset playback context
              key={screenerKey}
              style={{ opacity: loaded ? 1 : 0 }}
              ref={iframeRef}
              onLoad={attemptStart}
              title={`${screenerKey}`}
              id="video_player"
              srcDoc={playerHtml || ""}
              allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndeePlayer;
